import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { licenseApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { CreditCard, Loader2, Shield, CheckCircle2, AlertTriangle, LogOut, ArrowRight, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const SetupPaymentPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);

  useEffect(() => {
    fetchLicense();
    fetchAccessStatus();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId && params.get('payment') === 'success') {
      pollPayment(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchLicense = async () => {
    try {
      const res = await licenseApi.getMy();
      setLicense(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessStatus = async () => {
    try {
      const res = await licenseApi.accessStatus();
      setAccessStatus(res.data);
      // If user already has access (trial, free_access, etc.), redirect
      if (res.data?.has_access) {
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const pollPayment = async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setPolling(false);
      toast.info('Ověření platby trvá déle. Obnovte stránku za chvíli.');
      return;
    }
    setPolling(true);
    try {
      const res = await licenseApi.checkoutStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setPolling(false);
        toast.success('Karta byla úspěšně přidána!');
        window.location.href = '/dashboard';
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2500);
    } catch (e) {
      setPolling(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const originUrl = window.location.origin;
      const res = await licenseApi.createSubscription(originUrl);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Chyba při přesměrování na platbu');
      setSubscribing(false);
    }
  };

  const handleSkip = async () => {
    // Refresh user data & access status then redirect
    if (refreshUser) await refreshUser();
    navigate('/dashboard', { replace: true });
  };

  const fmtCZK = (n) => new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK' }).format(n || 0);

  const trialDays = accessStatus?.trial_days_remaining || 0;
  const isFreeAccess = accessStatus?.reason === 'free_access';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <span className="text-3xl font-bold">
            <span className="text-foreground">energo</span>
            <span className="text-primary">mat</span>
          </span>
        </div>

        <Card className="border-primary/20 shadow-lg" data-testid="setup-payment-card">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-7 h-7 text-primary" />
            </div>
            <CardTitle className="text-xl">Přidejte platební kartu</CardTitle>
            <CardDescription>
              Pro plný přístup k energomatu přidejte platební kartu.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : polling ? (
              <div className="flex flex-col items-center py-6 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Ověřuji platbu...</p>
              </div>
            ) : (
              <>
                {/* Plan summary */}
                {license && (
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Váš plán</span>
                      <span className="font-semibold">{license.buildings_count} budov, {license.units_count} jednotek</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Měsíční poplatek</span>
                      <span className="font-bold text-primary">{fmtCZK(license.monthly_total || license.monthly_cost)}</span>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="w-full h-12 text-base"
                  data-testid="setup-payment-btn"
                >
                  {subscribing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Přesměrovávám...</>
                  ) : (
                    <><CreditCard className="w-5 h-5 mr-2" /> Přidat kartu a aktivovat</>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Zabezpečeno Stripe</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Zrušení kdykoliv</span>
                </div>

                {/* Skip / Trial button */}
                <div className="pt-2 border-t space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleSkip}
                    data-testid="skip-payment-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Pokračovat bez karty
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground">
                    Kartu můžete přidat později v nastavení.
                  </p>
                </div>
              </>
            )}

            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={logout}
                data-testid="setup-payment-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Odhlásit se
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetupPaymentPage;
